import type { JsonObject } from "@/types/json"
import type { ScriptTurn } from "@/types/ScriptTurn"

export interface CoviDraftTestCase {
  input_str: string
  description?: string
  expected_behaviors?: string[]
  expected_output_json?: JsonObject
  /** "SCENARIO" (default) or "SCRIPT". */
  input_type?: string
  /** Only meaningful with input_type "SCRIPT". */
  script_turns?: ScriptTurn[]
}

/** One sparse change to an existing case — the args of update_test_case. */
export interface CoviTestCasePatch {
  test_case_id: string
  input_str?: string
  /**
   * Only in an update_test_cases entry, instead of input_str: the text the
   * panel adds after the case's current simulation input.
   */
  append_input_str?: string
  description?: string
  expected_behaviors?: string[]
  /** Attribute patch: a null value removes that attribute. */
  expected_output_json?: JsonObject
  input_type?: string
  script_turns?: ScriptTurn[]
}

export type CoviTestSetEditorAction =
  | {
      kind: "draft_test_cases"
      args: {
        test_set_id?: string
        test_cases: CoviDraftTestCase[]
        /**
         * The full count the user asked for when it exceeds this batch
         * (lockstep with sofia's draft_test_cases requested_total arg).
         * Threaded so this type matches parseActionArgs output; the bridge
         * never reads it — the staged receipt is per-batch only, and
         * cumulative progress/continuation gating derive from the
         * transcript's batch sizes in app-covi draftBatchProgress.
         */
        requested_total?: number
      }
    }
  | {
      kind: "add_test_case"
      args: CoviDraftTestCase & { test_set_id: string }
    }
  | {
      kind: "update_test_case"
      args: CoviTestCasePatch
    }
  | {
      /**
       * One staged batch of sparse changes to a saved set's existing cases
       * (lockstep with sofia's update_test_cases spec). Every change stages
       * under one registration, so the tray shows one batch with one Save.
       */
      kind: "update_test_cases"
      args: { test_set_id: string; changes: CoviTestCasePatch[] }
    }
  | {
      kind: "delete_test_case"
      args: { test_case_id: string }
    }

export interface CoviTestSetPendingItem {
  id: string
  label: string
  added: number
  removed: number
}

export interface CoviTestSetEditorSnapshot {
  active: boolean
  testSetId: string
  testCaseIds: string[]
  pendingType: "ADD" | "EDIT" | "DELETE" | null
  items: CoviTestSetPendingItem[]
}

export interface CoviTestSetEditorResult {
  ok: boolean
  detail?: string
  error?: string
}

// Saving the test set is the only accept step, so the tray drives the same
// save the page header does rather than accepting rows on its own.
interface CoviTestSetEditorController {
  stage: (action: CoviTestSetEditorAction) => CoviTestSetEditorResult
  save: () => CoviTestSetEditorResult
  undoAll: () => void
  undoItem: (index: number) => void
  scrollToItem: (id: string) => void
}

const EMPTY_SNAPSHOT: CoviTestSetEditorSnapshot = {
  active: false,
  testSetId: "",
  testCaseIds: [],
  pendingType: null,
  items: [],
}

let activeRegistration: symbol | null = null
let activeController: CoviTestSetEditorController | null = null
let snapshot = EMPTY_SNAPSHOT
let mutationInFlight = false
let saveInFlight = false
const listeners = new Set<() => void>()

function emitChange() {
  listeners.forEach((listener) => listener())
}

export function registerCoviTestSetEditor(
  controller: CoviTestSetEditorController,
): { registration: symbol; unregister: () => void } {
  const registration = Symbol("covi-test-set-editor")
  activeRegistration = registration
  activeController = controller
  snapshot = EMPTY_SNAPSHOT
  mutationInFlight = false
  emitChange()

  return {
    registration,
    unregister: () => {
      if (activeRegistration !== registration) {
        return
      }
      activeRegistration = null
      activeController = null
      snapshot = EMPTY_SNAPSHOT
      mutationInFlight = false
      emitChange()
    },
  }
}

export function publishCoviTestSetEditorSnapshot(
  registration: symbol,
  nextSnapshot: CoviTestSetEditorSnapshot,
): void {
  if (activeRegistration !== registration) {
    return
  }
  snapshot = nextSnapshot
  mutationInFlight = false
  emitChange()
}

export function subscribeCoviTestSetEditor(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getCoviTestSetEditorSnapshot(): CoviTestSetEditorSnapshot {
  return snapshot
}

export function getCoviTestSetEditorServerSnapshot(): CoviTestSetEditorSnapshot {
  return EMPTY_SNAPSHOT
}

export function tryStageCoviTestSetEditorAction(
  action: CoviTestSetEditorAction,
  expectedRegistration?: symbol,
): CoviTestSetEditorResult | null {
  if (
    expectedRegistration !== undefined &&
    activeRegistration !== expectedRegistration
  ) {
    return null
  }
  if (!activeController) {
    return action.kind === "draft_test_cases"
      ? {
          ok: false,
          error: "Open a test set editor before drafting test cases.",
        }
      : null
  }

  if (!matchesActiveTestSet(action.kind, action.args)) {
    return null
  }

  if (!editsLoadedCases(action.kind, action.args)) {
    return null
  }

  return activeController.stage(action)
}

// The editor header owns the save; it writes that state here so a tray save
// can be refused synchronously instead of dispatching a SAVE_REQUEST the
// header silently declines while busy. A declined request would leave the tray
// latched as saving until an unrelated snapshot arrived.
export function setCoviTestSetEditorSaveInFlight(value: boolean): void {
  saveInFlight = value
}

export function isCoviTestSetEditorSaveInFlight(): boolean {
  return saveInFlight
}

export function trySaveCoviTestSetEditor(): CoviTestSetEditorResult {
  if (!activeController) {
    return {
      ok: false,
      error: "Open a test set editor before saving test cases.",
    }
  }
  if (mutationInFlight) {
    return {
      ok: false,
      error: "A test-case change is already being applied.",
    }
  }
  if (saveInFlight) {
    return {
      ok: false,
      error: "The test set is already being saved.",
    }
  }

  mutationInFlight = true
  const result = activeController.save()
  // A refused save never publishes a new snapshot to clear the flag.
  if (!result.ok) {
    mutationInFlight = false
  }
  return result
}

export function undoAllCoviTestSetChanges(): void {
  runPendingMutation(() => activeController?.undoAll())
}

export function undoCoviTestSetChange(index: number): void {
  runPendingMutation(() => activeController?.undoItem(index))
}

export function scrollToCoviTestSetChange(id: string): void {
  activeController?.scrollToItem(id)
}

function runPendingMutation(mutate: () => void): void {
  if (!activeController || mutationInFlight) {
    return
  }
  mutationInFlight = true
  mutate()
}

export function canStageCoviTestSetEditorAction(
  kind: string,
  args: object,
): boolean {
  return getCoviTestSetEditorRegistration(kind, args) !== null
}

export function getCoviTestSetEditorRegistration(
  kind: string,
  args: object,
): symbol | null {
  if (!activeController) {
    return null
  }
  if (
    kind !== "draft_test_cases" &&
    kind !== "add_test_case" &&
    kind !== "update_test_case" &&
    kind !== "update_test_cases" &&
    kind !== "delete_test_case"
  ) {
    return null
  }
  if (!matchesActiveTestSet(kind, args)) {
    return null
  }

  return editsLoadedCases(kind, args) ? activeRegistration : null
}

/**
 * The ids of the existing cases an action edits or deletes, or null for an
 * action that only adds rows. A malformed id reads as "" so it can never match.
 */
function editedCaseIds(kind: string, args: object): string[] | null {
  if (kind === "update_test_case" || kind === "delete_test_case") {
    const id = "test_case_id" in args ? args.test_case_id : undefined
    return [typeof id === "string" ? id : ""]
  }
  if (kind === "update_test_cases") {
    const changes = "changes" in args ? args.changes : undefined
    if (!Array.isArray(changes) || changes.length === 0) {
      return [""]
    }
    return changes.map((change: unknown) =>
      typeof change === "object" &&
      change !== null &&
      "test_case_id" in change &&
      typeof change.test_case_id === "string"
        ? change.test_case_id
        : "",
    )
  }
  return null
}

/** Whether every case the action touches is loaded in the active editor. */
/**
 * Whether the active editor can take the action. A single-case edit needs its
 * case loaded. A batch needs every id well-formed and at least one loaded: the
 * editor stages the loaded rows and its receipt names each row it skipped, so
 * one deleted case never blocks the rest of a set-wide edit.
 */
function editsLoadedCases(kind: string, args: object): boolean {
  const ids = editedCaseIds(kind, args)
  if (ids === null) {
    return true
  }
  const loaded = (id: string) => snapshot.testCaseIds.includes(id)
  return kind === "update_test_cases"
    ? ids.every((id) => id.length > 0) && ids.some(loaded)
    : ids.every(loaded)
}

export function waitForCoviTestSetEditorRegistration(
  kind: string,
  args: object,
  timeoutMs = 10_000,
): Promise<symbol | null> {
  const currentRegistration = getCoviTestSetEditorRegistration(kind, args)
  if (currentRegistration) {
    return Promise.resolve(currentRegistration)
  }

  return new Promise((resolve) => {
    let unsubscribe = () => {}
    const timeout = globalThis.setTimeout(() => {
      unsubscribe()
      resolve(null)
    }, timeoutMs)
    unsubscribe = subscribeCoviTestSetEditor(() => {
      const registration = getCoviTestSetEditorRegistration(kind, args)
      if (!registration) {
        return
      }
      globalThis.clearTimeout(timeout)
      unsubscribe()
      resolve(registration)
    })
  })
}

function matchesActiveTestSet(kind: string, args: object): boolean {
  if (kind === "update_test_case" || kind === "delete_test_case") {
    return true
  }
  const testSetId = "test_set_id" in args ? args.test_set_id : undefined
  if (kind === "draft_test_cases" && snapshot.testSetId === "") {
    return testSetId === undefined || testSetId === ""
  }
  return (
    typeof testSetId === "string" &&
    testSetId.length > 0 &&
    testSetId === snapshot.testSetId
  )
}
